import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { useQuery } from '@tanstack/react-query';

import {
  clientDeactivationFromDto,
  clientGroupDeactivationFromDto,
} from '../../lib';
import type {
  ClientDeactivation,
  ClientDeactivationDto,
  ClientGroupDeactivation,
  ClientGroupDeactivationDto,
} from '../../types';
import { clientGroupUrl, clientUrl } from './constants';
import { clientDetailKey, clientGroupDetailKey } from './keys';

/**
 * What deactivating the client does, for its confirmation dialog: loaded
 * when the dialog opens and again each time, since the group may change.
 */
export function useClientDeactivation(
  clientId: number | string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const apiClient = useAPIClient();
  return useQuery({
    queryKey: [...clientDetailKey(clientId), 'deactivation'],
    enabled,
    staleTime: 0,
    gcTime: 0,
    queryFn: ({ signal }) =>
      apiClient.request({
        url: `${clientUrl(clientId)}deactivation/`,
        options: { signal },
      }) as Promise<ClientDeactivationDto>,
    select: (data): ClientDeactivation => clientDeactivationFromDto(data),
  });
}

/**
 * What deactivating the group does, for its confirmation dialog: loaded
 * when the dialog opens and again each time.
 */
export function useClientGroupDeactivation(
  groupId: number | string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const apiClient = useAPIClient();
  return useQuery({
    queryKey: [...clientGroupDetailKey(groupId), 'deactivation'],
    enabled,
    staleTime: 0,
    gcTime: 0,
    queryFn: ({ signal }) =>
      apiClient.request({
        url: `${clientGroupUrl(groupId)}deactivation/`,
        options: { signal },
      }) as Promise<ClientGroupDeactivationDto>,
    select: (data): ClientGroupDeactivation =>
      clientGroupDeactivationFromDto(data),
  });
}
